import React, { useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useUserContext } from '@/auth/UserContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useResetUnseenEvents } from '@/hooks/useResetUnseenEvents';
import { useUnseenEvents } from '@/hooks/useUnseenEvents';
import { Layout } from '@/layouts/Layout';
import { useAbsenceApplication } from '@/components/AbsenceApplication/useAbsenceApplication';
import { ExpirationBanner } from '@/components/ExpirationBanner';
import { useExpirationBanner } from '@/components/ExpirationBanner/hooks/useExpirationBanner';
import { NoticeBanner } from '@/components/NoticeBanner';
import { useDashboardNotice } from '@/components/NoticeBanner/hooks/useDashboardNotice';
import { useHideDashboardNotice } from '@/components/NoticeBanner/hooks/useHideDashboardNotice';
import { ProfileStatusBanner } from '@/components/ProfileStatusBanner';
import { Dashboard } from '@/components/Dashboard';
import AdBanner from '@/components/AdBanner';
import { HomeDiary } from '@/pages/Home/HomeDiary';
import { usePostPaymentRefresh } from '@/pages/Home/hooks/usePostPaymentRefresh';
import { News } from '@/pages/Home/News';
import { VideoMeetings } from '@/pages/Home/VideoMeetings';
import { ROUTE_HOME, ROUTE_OVERVIEW } from '@/router/routes';
import { PARENT_ROLES } from '@/types/user';
import { HeaderCard } from '@/components/Cards/HeaderCard';
import { Snackbar } from '@/components/Snackbar';
import { Anchor } from '@/types/anchors';
import {
  HeaderCardIcon,
  SnackbarVariant,
  UnseenEventType,
} from '@/types/components';
import { SCROLL_TO_START, scrollToElement } from '@/utils/scroll';

const HOME_SECTION_EVENT_TYPES = [
  UnseenEventType.Evaluation,
  UnseenEventType.BehaviorEntry,
  UnseenEventType.DiaryNote,
  UnseenEventType.NonAttendance,
];

export const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state: { subscriptionSuccess?: boolean } | null;
  };
  const { user } = useUserContext();
  const { data: unseenEvents } = useUnseenEvents();
  const resetUnseenEvents = useResetUnseenEvents();
  const isMobile = useIsMobile();
  const { data: notice } = useDashboardNotice();
  const { hide, isPending } = useHideDashboardNotice();
  const subscription = useExpirationBanner();
  const { onOpenList } = useAbsenceApplication();
  usePostPaymentRefresh();
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(
    Boolean(state?.subscriptionSuccess),
  );

  const canReportAbsence =
    !!user?.personType && PARENT_ROLES.has(user.personType);
  const newsCount = unseenEvents?.newsCount.total ?? 0;
  const isUnseenEventsLoaded = !!unseenEvents;

  const scrollToNews = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    scrollToElement(Anchor.News, SCROLL_TO_START);
  };

  const headerCards = (
    <>
      {canReportAbsence && (
        <HeaderCard
          label={t('absenceHistory.title')}
          icon={HeaderCardIcon.Absence}
          bgClassName="bg-warning-surface"
          onClick={onOpenList}
        />
      )}
      <HeaderCard
        to={ROUTE_OVERVIEW}
        label={t('home.readNews')}
        icon={HeaderCardIcon.News}
        iconClassName="mt-1"
        bgClassName="bg-blue-soft"
        badgeCount={newsCount}
        onClick={scrollToNews}
      />
    </>
  );

  const resetHomeSections = useEffectEvent(async () => {
    await resetUnseenEvents(HOME_SECTION_EVENT_TYPES);
  });

  useEffect(() => {
    if (state?.subscriptionSuccess) {
      navigate(ROUTE_HOME, { replace: true });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (isMobile || !isUnseenEventsLoaded) {
      return;
    }

    resetHomeSections();
  }, [isMobile, isUnseenEventsLoaded]);

  return (
    <Layout desktopHeader={headerCards}>
      <ProfileStatusBanner />
      {subscription && <ExpirationBanner subscription={subscription} />}
      {notice && (
        <NoticeBanner
          notice={notice}
          isPending={isPending}
          onClose={() => hide(notice.id)}
        />
      )}
      <div className="flex gap-3 px-4 py-3 md:hidden">{headerCards}</div>
      <Dashboard />
      <AdBanner className="md:pt-10!" />
      <VideoMeetings />
      <HomeDiary />
      <News />

      <Snackbar
        isOpen={showSubscriptionSuccess}
        compact
        message={t('subscription.checkout.smsSuccess')}
        variant={SnackbarVariant.Success}
        onDismiss={() => setShowSubscriptionSuccess(false)}
      />
    </Layout>
  );
};
